import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Wallet, Plus, ReceiptText, RotateCcw, Paperclip, ArrowDownLeft, ArrowUpRight, FileText, Download, X } from 'lucide-react';
import { DateInput } from '@/components/ui/date-input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_STYLES = {
  approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/40',
  settled: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
  reversed: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/40',
};

const TYPE_LABELS = {
  driver_handover: 'From Driver', direct_receipt: 'Cash Received', expense: 'Expense',
  submitted_to_admin: 'To Admin', adjustment: 'Adjustment', settlement: 'Settlement',
  pos_shift_cash: 'POS Shift Cash',
};

export const StatusBadge = ({ status }) => (
  <Badge className={`text-[10px] ${STATUS_STYLES[status] || STATUS_STYLES.reversed}`}>{status}</Badge>
);

export const LedgerTable = ({ ledger, formatCurrency, onAdjust }) => (
  <div className="overflow-x-auto" data-testid="manager-cash-ledger">
    <table className="w-full text-xs">
      <thead>
        <tr className="text-wt-text-muted border-b border-wt-border/50 text-left">
          <th className="py-2 pr-2">Date</th><th className="pr-2">Type</th><th className="pr-2">Order</th>
          <th className="pr-2">Details</th><th className="pr-2 text-right">Amount</th>
          <th className="pr-2">Status</th><th className="pr-2 text-right">Balance</th><th></th>
        </tr>
      </thead>
      <tbody>
        {ledger.map((e) => (
          <tr key={e.id} className="border-b border-wt-border/30 text-wt-text-primary" data-testid={`ledger-row-${e.id}`}>
            <td className="py-2 pr-2 whitespace-nowrap text-wt-text-muted">{(e.at || '').slice(0, 16).replace('T', ' ')}</td>
            <td className="pr-2 whitespace-nowrap">
              <span className="flex items-center gap-1">
                {e.direction === 'in' ? <ArrowDownLeft className="w-3 h-3 text-emerald-400" /> : <ArrowUpRight className="w-3 h-3 text-red-400" />}
                {TYPE_LABELS[e.type] || e.type}
              </span>
            </td>
            <td className="pr-2">{e.order_number || '—'}</td>
            <td className="pr-2 max-w-[220px] truncate" title={e.notes}>{e.type === 'pos_shift_cash' ? 'POS shift handover · ' : e.category ? `${e.category} · ` : ''}{e.notes || e.source || ''}</td>
            <td className={`pr-2 text-right font-medium ${e.direction === 'in' ? 'text-emerald-400' : 'text-red-400'}`}>
              {e.direction === 'in' ? '+' : '−'}{formatCurrency(e.amount)}
            </td>
            <td className="pr-2"><StatusBadge status={e.status} /></td>
            <td className="pr-2 text-right text-wt-text-muted">{formatCurrency(e.running_balance)}</td>
            <td>
              {onAdjust && ['direct_receipt', 'expense'].includes(e.type) && ['approved'].includes(e.status) && !e.has_pending_adjustment && (
                <Button variant="ghost" size="icon" className="h-6 w-6 text-wt-text-muted" title="Request reversal/adjustment"
                  onClick={() => onAdjust(e)} data-testid={`adjust-btn-${e.id}`}>
                  <RotateCcw className="w-3 h-3" />
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {ledger.length === 0 && <p className="text-center text-wt-text-muted text-sm py-6">No transactions yet</p>}
  </div>
);

export const WalletStats = ({ wallet, formatCurrency }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="manager-wallet-stats">
    <div className="rounded-lg border border-wt-border/50 p-3">
      <p className="text-[11px] text-wt-text-muted">Cash from drivers (held)</p>
      <p className="text-lg font-semibold text-wt-text-primary">{formatCurrency(wallet.cash_from_drivers_held)}</p>
    </div>
    <div className="rounded-lg border border-wt-border/50 p-3">
      <p className="text-[11px] text-wt-text-muted">Direct receipts {wallet.direct_receipts_pending > 0 && <span className="text-amber-400">(+{formatCurrency(wallet.direct_receipts_pending)} pending)</span>}</p>
      <p className="text-lg font-semibold text-emerald-400">{formatCurrency(wallet.direct_receipts_approved)}</p>
    </div>
    <div className="rounded-lg border border-wt-border/50 p-3">
      <p className="text-[11px] text-wt-text-muted">Expenses from cash {wallet.expenses_pending > 0 && <span className="text-amber-400">(−{formatCurrency(wallet.expenses_pending)} pending)</span>}</p>
      <p className="text-lg font-semibold text-red-400">{formatCurrency(wallet.expenses_approved)}</p>
    </div>
    <div className="rounded-lg border border-wt-gold/40 p-3 bg-wt-gold/5">
      <p className="text-[11px] text-wt-text-muted">Current cash balance</p>
      <p className="text-lg font-bold text-wt-gold" data-testid="wallet-balance">{formatCurrency(wallet.current_balance)}</p>
    </div>
  </div>
);

const FileAttach = ({ attachments, setAttachments }) => {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const resp = await axios.post(`${API}/manager-cash/attachments`, fd, auth());
      setAttachments([...attachments, resp.data]);
      toast.success('Receipt attached');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };
  return (
    <div className="space-y-1.5">
      <input type="file" id="mc-file" className="hidden" accept="image/*,.pdf" onChange={onFile} />
      <Button type="button" variant="outline" size="sm" className="h-8 text-xs border-wt-border"
        disabled={uploading} onClick={() => document.getElementById('mc-file').click()} data-testid="mc-attach-btn">
        <Paperclip className="w-3 h-3 mr-1" />{uploading ? 'Uploading…' : 'Attach receipt'}
      </Button>
      {attachments.map((a, i) => (
        <div key={a.id || i} className="flex items-center justify-between text-xs bg-wt-bg/40 border border-wt-border/50 rounded px-2 py-1"
          data-testid={`mc-attachment-${a.id || i}`}>
          <span className="flex items-center gap-1 truncate text-wt-text-primary">
            <Paperclip className="w-3 h-3 shrink-0" />{a.name}
            {a.size ? <span className="text-wt-text-muted">· {(a.size / 1024).toFixed(1)} KB</span> : null}
          </span>
          <button type="button" className="text-rose-400 hover:text-rose-300 ml-2 shrink-0" title="Remove file"
            data-testid={`mc-remove-attachment-${a.id || i}`}
            onClick={() => setAttachments(attachments.filter((x) => x !== a))}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};

const emptyReceipt = () => ({ amount: '', category: '', order_number: '', customer_name: '', customer_phone: '', notes: '', attachments: [] });
const emptyExpense = () => ({ title: '', category_id: '', amount: '', expense_date: new Date().toISOString().slice(0, 10), description: '', vendor_name: '', invoice_number: '', attachments: [] });

export default function ManagerCashPanel({ formatCurrency }) {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showExpense, setShowExpense] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustReason, setAdjustReason] = useState('');
  const [receipt, setReceipt] = useState(emptyReceipt());
  const [expense, setExpense] = useState(emptyExpense());
  const [orderResults, setOrderResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [range, setRange] = useState({ from: '', to: '' });
  const [downloading, setDownloading] = useState(false);

  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const qs = new URLSearchParams();
      if (range.from) qs.set('date_from', range.from);
      if (range.to) qs.set('date_to', range.to);
      const resp = await axios.get(`${API}/manager-cash/statement.pdf?${qs}`, { ...auth(), responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(resp.data);
      link.download = 'my_cash_statement.pdf';
      link.click();
      URL.revokeObjectURL(link.href);
      setShowStatement(false);
    } catch { toast.error('Statement download failed'); }
    finally { setDownloading(false); }
  };

  const load = useCallback(async () => {
    try {
      const [l, s, c] = await Promise.all([
        axios.get(`${API}/manager-cash/ledger`, auth()),
        axios.get(`${API}/manager-cash/settings`, auth()),
        axios.get(`${API}/expenses/categories`, auth()),
      ]);
      setData(l.data);
      setSettings(s.data);
      setCategories(c.data.categories || c.data || []);
    } catch (e) { /* silent — permission or empty */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const searchOrders = async (q) => {
    setReceipt((r) => ({ ...r, order_number: q }));
    if (q.length < 2) { setOrderResults([]); return; }
    try {
      const resp = await axios.get(`${API}/manager-cash/orders/search?q=${encodeURIComponent(q)}`, auth());
      setOrderResults(resp.data.orders || []);
    } catch { setOrderResults([]); }
  };

  const submitReceipt = async () => {
    setSaving(true);
    try {
      const body = { ...receipt, amount: parseFloat(receipt.amount) || 0 };
      const resp = await axios.post(`${API}/manager-cash/receipts`, body, auth());
      toast.success(resp.data.status === 'pending' ? 'Cash receipt recorded — awaiting admin approval' : 'Cash receipt recorded');
      setShowReceipt(false);
      setReceipt(emptyReceipt());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record receipt'); }
    finally { setSaving(false); }
  };

  const submitExpense = async () => {
    setSaving(true);
    try {
      const body = { ...expense, amount: parseFloat(expense.amount) || 0 };
      await axios.post(`${API}/manager-cash/expenses`, body, auth());
      toast.success('Expense created from manager cash — pending approval');
      setShowExpense(false);
      setExpense(emptyExpense());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create expense'); }
    finally { setSaving(false); }
  };

  const submitAdjustment = async () => {
    try {
      await axios.post(`${API}/manager-cash/adjustments`, { target_entry_id: adjustTarget.id, reason: adjustReason }, auth());
      toast.success('Reversal request sent for admin approval');
      setAdjustTarget(null); setAdjustReason('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!data) return null;

  return (
    <div className="bg-wt-paper border border-wt-border rounded-xl p-6" data-testid="manager-cash-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-wt-gold/10"><Wallet className="h-6 w-6 text-wt-gold" /></div>
          <div>
            <h2 className="text-xl font-semibold text-wt-text-primary">My Cash Wallet</h2>
            <p className="text-wt-text-muted text-sm">Live ledger — driver cash, direct receipts & expenses</p>
          </div>
        </div>
        <div className="flex gap-2">
          {settings?.allow_manager_direct_receipt && (
            <Button size="sm" onClick={() => { setReceipt(emptyReceipt()); setOrderResults([]); setShowReceipt(true); }} data-testid="record-cash-received-btn">
              <Plus className="w-4 h-4 mr-1" />Record Cash Received
            </Button>
          )}
          <Button size="sm" variant="outline" className="border-wt-border" onClick={() => { setExpense(emptyExpense()); setShowExpense(true); }} data-testid="add-cash-expense-btn">
            <ReceiptText className="w-4 h-4 mr-1" />Expense from Cash
          </Button>
          <Button size="sm" variant="outline" className="border-wt-border" onClick={() => setShowStatement(true)} data-testid="my-statement-btn">
            <FileText className="w-4 h-4 mr-1" />My Statement
          </Button>
        </div>
      </div>

      <WalletStats wallet={data.wallet} formatCurrency={formatCurrency} />
      <LedgerTable ledger={data.ledger} formatCurrency={formatCurrency} onAdjust={setAdjustTarget} />

      {/* Record Cash Received dialog */}
      <Dialog open={showReceipt} onOpenChange={(o) => { setShowReceipt(o); if (o) { setReceipt(emptyReceipt()); setOrderResults([]); } }}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Record Cash Received</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-wt-text-muted">Amount *</Label>
                <Input type="number" value={receipt.amount} className="mt-1" data-testid="receipt-amount-input"
                  onChange={(e) => setReceipt({ ...receipt, amount: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Category *</Label>
                <Select value={receipt.category} onValueChange={(v) => setReceipt({ ...receipt, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="receipt-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {(settings?.allowed_receipt_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="relative">
              <Label className="text-xs text-wt-text-muted">Order number (optional — search)</Label>
              <Input value={receipt.order_number} className="mt-1" placeholder="Type to search orders…" data-testid="receipt-order-input"
                onChange={(e) => searchOrders(e.target.value)} />
              {orderResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full rounded border border-wt-border bg-wt-surface max-h-40 overflow-auto">
                  {orderResults.map((o) => (
                    <button key={o.id} type="button" className="w-full text-left px-3 py-1.5 text-xs hover:bg-wt-gold/10 text-wt-text-primary"
                      data-testid={`order-result-${o.order_number}`}
                      onClick={() => { setReceipt({ ...receipt, order_number: o.order_number, customer_name: o.customer_name || '' }); setOrderResults([]); }}>
                      {o.order_number} · {o.customer_name} · {formatCurrency(o.total_amount)} ({o.payment_status})
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-wt-text-muted">Customer name</Label>
                <Input value={receipt.customer_name} className="mt-1" data-testid="receipt-customer-input"
                  onChange={(e) => setReceipt({ ...receipt, customer_name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Customer phone</Label>
                <Input value={receipt.customer_phone} className="mt-1"
                  onChange={(e) => setReceipt({ ...receipt, customer_phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-wt-text-muted">Notes {!receipt.order_number && '(required without order)'}</Label>
              <Textarea value={receipt.notes} rows={2} className="mt-1" data-testid="receipt-notes-input"
                onChange={(e) => setReceipt({ ...receipt, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitReceipt} disabled={saving} data-testid="receipt-submit-btn">{saving ? 'Saving…' : 'Record Receipt'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense from cash dialog */}
      <Dialog open={showExpense} onOpenChange={(o) => { setShowExpense(o); if (o) setExpense(emptyExpense()); }}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Expense Paid From Manager Cash</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-wt-text-muted">Title *</Label>
              <Input value={expense.title} className="mt-1" data-testid="expense-title-input"
                onChange={(e) => setExpense({ ...expense, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-wt-text-muted">Category *</Label>
                <Select value={expense.category_id} onValueChange={(v) => setExpense({ ...expense, category_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="expense-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Amount *</Label>
                <Input type="number" value={expense.amount} className="mt-1" data-testid="expense-amount-input"
                  onChange={(e) => setExpense({ ...expense, amount: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Date</Label>
                <DateInput  value={expense.expense_date} className="mt-1"
                  onChange={(e) => setExpense({ ...expense, expense_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-wt-text-muted">Vendor (optional)</Label>
                <Input value={expense.vendor_name} className="mt-1" data-testid="expense-vendor-input"
                  onChange={(e) => setExpense({ ...expense, vendor_name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Invoice number (optional)</Label>
                <Input value={expense.invoice_number} className="mt-1" data-testid="expense-invoice-input"
                  onChange={(e) => setExpense({ ...expense, invoice_number: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-wt-text-muted">Notes</Label>
              <Textarea value={expense.description} rows={2} className="mt-1"
                onChange={(e) => setExpense({ ...expense, description: e.target.value })} />
            </div>
            <p className="text-[11px] text-wt-text-muted">Paid from: <b className="text-wt-gold">Cash With Manager</b> — also appears in Expense Management for approval.</p>
            <FileAttach attachments={expense.attachments} setAttachments={(a) => setExpense({ ...expense, attachments: a })} />
            {settings && parseFloat(expense.amount || 0) >= settings.receipt_required_above && expense.attachments.length === 0 && (
              <p className="text-[11px] text-amber-400">Receipt/proof required for {formatCurrency(settings.receipt_required_above)} and above</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={submitExpense} disabled={saving} data-testid="expense-submit-btn">{saving ? 'Saving…' : 'Create Expense'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* My Statement dialog */}
      <Dialog open={showStatement} onOpenChange={setShowStatement}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-sm">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Download My Cash Statement</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-wt-text-muted">From</Label>
              <DateInput  value={range.from} max={range.to || undefined} className="mt-1" data-testid="my-statement-from"
                onChange={(e) => setRange({ ...range, from: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-wt-text-muted">To</Label>
              <DateInput  value={range.to} min={range.from || undefined} className="mt-1" data-testid="my-statement-to"
                onChange={(e) => setRange({ ...range, to: e.target.value })} />
            </div>
          </div>
          <p className="text-[11px] text-wt-text-muted">Leave empty for the full history.</p>
          <DialogFooter>
            <Button onClick={downloadStatement} disabled={downloading} data-testid="my-statement-download-btn">
              <Download className="w-4 h-4 mr-2" />{downloading ? 'Generating…' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjustment dialog */}
      <Dialog open={!!adjustTarget} onOpenChange={(o) => !o && setAdjustTarget(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Request Reversal / Adjustment</DialogTitle></DialogHeader>
          <p className="text-xs text-wt-text-muted">
            {adjustTarget && `${TYPE_LABELS[adjustTarget.type]} · ${formatCurrency(adjustTarget.amount)} — a reversal request will be sent for admin approval. Approved transactions cannot be edited or deleted directly.`}
          </p>
          <Textarea value={adjustReason} rows={2} placeholder="Reason for reversal *" data-testid="adjust-reason-input"
            onChange={(e) => setAdjustReason(e.target.value)} />
          <DialogFooter>
            <Button onClick={submitAdjustment} disabled={!adjustReason.trim()} data-testid="adjust-submit-btn">Send Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
